import { useState, useRef, useEffect, useCallback } from 'react';
import { Layer, PropInstance, TextInstance, PenPathInstance } from '../../services/api';
import Modal from '../Modal/Modal';
import './LayerSelector.css';

/** Unified layer item for display in the panel */
export interface LayerItem {
  id: string;
  type: 'prop' | 'text' | 'path' | 'shape' | 'shape';
  name: string;
  layerId: string;
  zIndex: number;
}

interface LayerSelectorProps {
  layers: Layer[];
  activeLayerId: string;
  onSelectLayer: (layerId: string) => void;
  onToggleVisibility: (layerId: string) => void;
  onAddLayer?: (name: string, parentId?: string | null) => void;
  onRenameLayer?: (layerId: string, name: string) => void;
  onDeleteLayer?: (layerId: string, deleteChildren?: boolean, deleteProps?: boolean) => void;
  onReorderLayers?: (layerIds: string[]) => void;
  onUpdateLayerOpacity: (layerId: string, opacity: number) => void;
  onMoveLayerToParent?: (layerId: string, parentId: string | null) => void;
  isOpen: boolean;
  onClose: () => void;
  /** Read-only mode - only visibility and opacity can be changed */
  readOnly?: boolean;
  /** Items (props, texts, paths) to display under layers */
  items?: LayerItem[];
  /** Currently selected item IDs */
  selectedItemIds?: string[];
  /** Callback when an item is selected */
  onSelectItem?: (itemId: string, itemType: 'prop' | 'text' | 'path' | 'shape' | 'shape') => void;
  /** Callback when items are reordered within a layer */
  onReorderItems?: (layerId: string, itemIds: string[], itemTypes: ('prop' | 'text' | 'path' | 'shape' | 'shape')[]) => void;
  /** Callback when an item is moved to a different layer */
  onMoveItemToLayer?: (itemId: string, itemType: 'prop' | 'text' | 'path' | 'shape' | 'shape', targetLayerId: string) => void;
  /** Callback to open description editor for a layer */
  onEditDescription?: (layerId: string) => void;
}

/**
 * Layer selector panel for managing map layers
 * Displays layers with visibility toggles, opacity controls, and drag-drop reordering
 */
function LayerSelector({
  layers,
  activeLayerId,
  onSelectLayer,
  onToggleVisibility,
  onAddLayer,
  onRenameLayer,
  onDeleteLayer,
  onReorderLayers,
  onUpdateLayerOpacity,
  onMoveLayerToParent,
  isOpen,
  onClose,
  readOnly = false,
  items = [],
  selectedItemIds = [],
  onSelectItem,
  onReorderItems,
  onMoveItemToLayer,
  onEditDescription
}: LayerSelectorProps): JSX.Element | null {
  const [isAddingLayer, setIsAddingLayer] = useState(false);
  const [addingToParentId, setAddingToParentId] = useState<string | null>(null);
  const [newLayerName, setNewLayerName] = useState('');
  const [editingLayerId, setEditingLayerId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deleteConfirmLayerId, setDeleteConfirmLayerId] = useState<string | null>(null);
  const [draggedLayerId, setDraggedLayerId] = useState<string | null>(null);
  const [dragOverInfo, setDragOverInfo] = useState<{ layerId: string; position: 'above' | 'below' | 'inside' } | null>(null);
  const [collapsedLayers, setCollapsedLayers] = useState<Set<string>>(new Set());
  const [expandedItemLayers, setExpandedItemLayers] = useState<Set<string>>(new Set());
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [draggedItemType, setDraggedItemType] = useState<'prop' | 'text' | 'path' | 'shape' | 'shape' | null>(null);
  const [itemDragOverInfo, setItemDragOverInfo] = useState<{ itemId: string; position: 'above' | 'below' } | null>(null);
  const [itemDragOverLayerId, setItemDragOverLayerId] = useState<string | null>(null);
  const addInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  /**
   * Get children of a layer
   */
  const getChildren = useCallback((parentId: string | null): Layer[] => {
    return layers.filter(l => l.parentId === parentId).sort((a, b) => b.order - a.order);
  }, [layers]);

  /**
   * Check if a layer has children
   */
  const hasChildren = useCallback((layerId: string): boolean => {
    return layers.some(l => l.parentId === layerId);
  }, [layers]);

  /**
   * Get all descendants of a layer
   */
  const getDescendants = useCallback((layerId: string): string[] => {
    const descendants: string[] = [];
    const children = layers.filter(l => l.parentId === layerId);
    for (const child of children) {
      descendants.push(child.id);
      descendants.push(...getDescendants(child.id));
    }
    return descendants;
  }, [layers]);

  /**
   * Toggle layer collapsed state
   */
  const toggleCollapsed = useCallback((layerId: string): void => {
    setCollapsedLayers(prev => {
      const next = new Set(prev);
      if (next.has(layerId)) {
        next.delete(layerId);
      } else {
        next.add(layerId);
      }
      return next;
    });
  }, []);

  /**
   * Check if any parent of a layer is hidden
   */
  const isParentHidden = useCallback((layerId: string): boolean => {
    const layer = layers.find(l => l.id === layerId);
    if (!layer || !layer.parentId) return false;

    const parent = layers.find(l => l.id === layer.parentId);
    if (!parent) return false;

    if (!parent.visible) return true;
    return isParentHidden(parent.id);
  }, [layers]);

  /**
   * Get items for a specific layer, sorted by zIndex (descending for display)
   */
  const getLayerItems = useCallback((layerId: string): LayerItem[] => {
    return items
      .filter(item => item.layerId === layerId)
      .sort((a, b) => b.zIndex - a.zIndex);
  }, [items]);

  /**
   * Check if a layer has items
   */
  const layerHasItems = useCallback((layerId: string): boolean => {
    return items.some(item => item.layerId === layerId);
  }, [items]);

  /**
   * Toggle items visibility for a layer
   */
  const toggleItemsExpanded = useCallback((layerId: string): void => {
    setExpandedItemLayers(prev => {
      const next = new Set(prev);
      if (next.has(layerId)) {
        next.delete(layerId);
      } else {
        next.add(layerId);
      }
      return next;
    });
  }, []);

  /**
   * Get icon class for item type
   */
  const getItemIcon = useCallback((type: 'prop' | 'text' | 'path' | 'shape'): string => {
    switch (type) {
      case 'prop': return 'fa-cube';
      case 'text': return 'fa-font';
      case 'path': return 'fa-bezier-curve';
      case 'shape': return 'fa-shapes';
      default: return 'fa-circle';
    }
  }, []);

  // Focus input when adding layer
  useEffect(() => {
    if (isAddingLayer && addInputRef.current) {
      addInputRef.current.focus();
    }
  }, [isAddingLayer]);

  // Focus input when editing layer
  useEffect(() => {
    if (editingLayerId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingLayerId]);

  /**
   * Handle adding a new layer
   */
  const handleAddLayer = (): void => {
    if (newLayerName.trim() && onAddLayer) {
      onAddLayer(newLayerName.trim(), addingToParentId);
      setNewLayerName('');
      setIsAddingLayer(false);
      setAddingToParentId(null);
    }
  };

  /**
   * Handle key press in add input
   */
  const handleAddKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      handleAddLayer();
    } else if (e.key === 'Escape') {
      setIsAddingLayer(false);
      setNewLayerName('');
      setAddingToParentId(null);
    }
  };

  /**
   * Start editing a layer name
   */
  const startEditing = (layer: Layer): void => {
    setEditingLayerId(layer.id);
    setEditingName(layer.name);
  };

  /**
   * Handle renaming a layer
   */
  const handleRename = (): void => {
    if (editingLayerId && editingName.trim() && onRenameLayer) {
      onRenameLayer(editingLayerId, editingName.trim());
    }
    setEditingLayerId(null);
    setEditingName('');
  };

  /**
   * Handle key press in edit input
   */
  const handleEditKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      handleRename();
    } else if (e.key === 'Escape') {
      setEditingLayerId(null);
      setEditingName('');
    }
  };

  /**
   * Handle delete confirmation
   * @param deleteChildren If true, also delete all child layers
   * @param deleteProps If true, also delete all props on deleted layers
   */
  const handleDeleteConfirm = (deleteChildren: boolean, deleteProps: boolean): void => {
    if (deleteConfirmLayerId && onDeleteLayer) {
      onDeleteLayer(deleteConfirmLayerId, deleteChildren, deleteProps);
      setDeleteConfirmLayerId(null);
    }
  };

  /**
   * Handle drag start
   */
  const handleDragStart = useCallback((e: React.DragEvent, layerId: string): void => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', layerId);
    setDraggedLayerId(layerId);
  }, []);

  /**
   * Handle drag over - detect if above, below, or inside center of target
   */
  const handleDragOver = useCallback((e: React.DragEvent, layerId: string): void => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // If we're dragging an item (not a layer), highlight the layer as drop target
    if (draggedItemId) {
      setItemDragOverLayerId(layerId);
      setDragOverInfo(null);
      return;
    }

    if (layerId === draggedLayerId) {
      setDragOverInfo(null);
      return;
    }

    // Cannot drop onto a descendant
    if (draggedLayerId && getDescendants(draggedLayerId).includes(layerId)) {
      setDragOverInfo(null);
      return;
    }

    // Get mouse position relative to the target element
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY - rect.top;
    const height = rect.height;

    // Divide into 3 zones: top 25%, middle 50%, bottom 25%
    let position: 'above' | 'below' | 'inside';
    if (mouseY < height * 0.25) {
      position = 'above';
    } else if (mouseY > height * 0.75) {
      position = 'below';
    } else {
      position = 'inside';
    }

    setDragOverInfo({ layerId, position });
  }, [draggedLayerId, draggedItemId, getDescendants]);

  /**
   * Handle drag leave
   */
  const handleDragLeave = useCallback((): void => {
    setDragOverInfo(null);
    setItemDragOverLayerId(null);
  }, []);

  /**
   * Handle drop - reorder layers or move to parent, or move item to layer
   */
  const handleDrop = useCallback((e: React.DragEvent, targetLayerId: string): void => {
    e.preventDefault();

    // If we're dropping an item onto a layer
    if (draggedItemId && draggedItemType) {
      const draggedItem = items.find(i => i.id === draggedItemId);
      if (draggedItem && draggedItem.layerId !== targetLayerId) {
        onMoveItemToLayer?.(draggedItemId, draggedItemType, targetLayerId);
      }
      setItemDragOverLayerId(null);
      setDraggedItemId(null);
      setDraggedItemType(null);
      return;
    }

    if (!draggedLayerId || draggedLayerId === targetLayerId || !dragOverInfo) {
      setDragOverInfo(null);
      setDraggedLayerId(null);
      return;
    }

    const targetLayer = layers.find(l => l.id === targetLayerId);
    if (!targetLayer) {
      setDragOverInfo(null);
      setDraggedLayerId(null);
      return;
    }

    // If dropping inside, move to target as parent
    if (dragOverInfo.position === 'inside') {
      onMoveLayerToParent?.(draggedLayerId, targetLayerId);
      setDragOverInfo(null);
      setDraggedLayerId(null);
      return;
    }

    // For above/below, check if we're in the same parent group
    const draggedLayer = layers.find(l => l.id === draggedLayerId);
    if (!draggedLayer) {
      setDragOverInfo(null);
      setDraggedLayerId(null);
      return;
    }

    // If different parents, move to target's parent first
    if (draggedLayer.parentId !== targetLayer.parentId) {
      onMoveLayerToParent?.(draggedLayerId, targetLayer.parentId);
    }

    // Get siblings in the target's parent group
    const siblings = layers
      .filter(l => l.parentId === targetLayer.parentId)
      .sort((a, b) => b.order - a.order);

    const currentOrder = siblings.map(l => l.id);
    const draggedIndex = currentOrder.indexOf(draggedLayerId);
    const targetIndex = currentOrder.indexOf(targetLayerId);

    if (targetIndex === -1) {
      setDragOverInfo(null);
      setDraggedLayerId(null);
      return;
    }

    // Remove dragged item if it's in this group
    if (draggedIndex !== -1) {
      currentOrder.splice(draggedIndex, 1);
    }

    // Calculate new index based on drop position
    let newIndex = currentOrder.indexOf(targetLayerId);
    if (dragOverInfo.position === 'below') {
      newIndex += 1;
    }

    // Insert at new position
    currentOrder.splice(newIndex, 0, draggedLayerId);

    // Call reorder with new order (reversed because display is highest-first)
    onReorderLayers?.([...currentOrder].reverse());

    setDragOverInfo(null);
    setDraggedLayerId(null);
  }, [draggedLayerId, dragOverInfo, layers, onReorderLayers, onMoveLayerToParent, draggedItemId, draggedItemType, items, onMoveItemToLayer]);

  /**
   * Handle drag end
   */
  const handleDragEnd = useCallback((): void => {
    setDraggedLayerId(null);
    setDragOverInfo(null);
    setItemDragOverLayerId(null);
  }, []);

  /**
   * Handle opacity input change
   */
  const handleOpacityInputChange = useCallback((layerId: string, value: string): void => {
    // Parse the input value, clamp to 0-100
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      const clamped = Math.max(0, Math.min(100, numValue));
      onUpdateLayerOpacity(layerId, clamped);
    }
  }, [onUpdateLayerOpacity]);

  /**
   * Handle opacity input blur - ensure valid value
   */
  const handleOpacityBlur = useCallback((layerId: string, value: string): void => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) {
      // Reset to 100 if invalid
      onUpdateLayerOpacity(layerId, 100);
    }
  }, [onUpdateLayerOpacity]);

  /**
   * Handle item drag start
   */
  const handleItemDragStart = useCallback((e: React.DragEvent, itemId: string, itemType: 'prop' | 'text' | 'path' | 'shape'): void => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `item:${itemId}:${itemType}`);
    setDraggedItemId(itemId);
    setDraggedItemType(itemType);
    e.stopPropagation();
  }, []);

  /**
   * Handle item drag over
   */
  const handleItemDragOver = useCallback((e: React.DragEvent, itemId: string): void => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';

    if (itemId === draggedItemId) {
      setItemDragOverInfo(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY - rect.top;
    const position: 'above' | 'below' = mouseY < rect.height / 2 ? 'above' : 'below';

    setItemDragOverInfo({ itemId, position });
  }, [draggedItemId]);

  /**
   * Handle item drag leave
   */
  const handleItemDragLeave = useCallback((): void => {
    setItemDragOverInfo(null);
  }, []);

  /**
   * Handle item drop
   */
  const handleItemDrop = useCallback((e: React.DragEvent, targetItemId: string, targetLayerId: string): void => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedItemId || !draggedItemType || draggedItemId === targetItemId || !itemDragOverInfo) {
      setItemDragOverInfo(null);
      setDraggedItemId(null);
      setDraggedItemType(null);
      return;
    }

    // Get items in the target layer
    const layerItemsList = getLayerItems(targetLayerId);
    const draggedItem = items.find(i => i.id === draggedItemId);
    if (!draggedItem) {
      setItemDragOverInfo(null);
      setDraggedItemId(null);
      setDraggedItemType(null);
      return;
    }

    // Check if we're moving to a different layer
    if (draggedItem.layerId !== targetLayerId) {
      onMoveItemToLayer?.(draggedItemId, draggedItemType, targetLayerId);
    }

    // Reorder items within the layer
    const currentOrder = layerItemsList.map(i => ({ id: i.id, type: i.type }));
    const draggedIndex = currentOrder.findIndex(i => i.id === draggedItemId);
    const targetIndex = currentOrder.findIndex(i => i.id === targetItemId);

    // Remove dragged item if in same layer
    if (draggedIndex !== -1) {
      currentOrder.splice(draggedIndex, 1);
    }

    // Insert at new position
    let newIndex = currentOrder.findIndex(i => i.id === targetItemId);
    if (newIndex === -1) newIndex = 0;
    if (itemDragOverInfo.position === 'below') {
      newIndex += 1;
    }

    currentOrder.splice(newIndex, 0, { id: draggedItemId, type: draggedItemType });

    // Call reorder callback
    onReorderItems?.(
      targetLayerId,
      currentOrder.map(i => i.id),
      currentOrder.map(i => i.type)
    );

    setItemDragOverInfo(null);
    setDraggedItemId(null);
    setDraggedItemType(null);
  }, [draggedItemId, draggedItemType, itemDragOverInfo, items, getLayerItems, onMoveItemToLayer, onReorderItems]);

  /**
   * Handle item drag end
   */
  const handleItemDragEnd = useCallback((): void => {
    setDraggedItemId(null);
    setDraggedItemType(null);
    setItemDragOverInfo(null);
    setItemDragOverLayerId(null);
  }, []);

  /**
   * Render a single item row
   */
  const renderItem = (item: LayerItem, depth: number): JSX.Element => {
    const isSelected = selectedItemIds.includes(item.id);
    const isDragging = draggedItemId === item.id;
    const isDragOverAbove = itemDragOverInfo?.itemId === item.id && itemDragOverInfo?.position === 'above';
    const isDragOverBelow = itemDragOverInfo?.itemId === item.id && itemDragOverInfo?.position === 'below';

    return (
      <div
        key={`item-${item.type}-${item.id}`}
        className={`layer-item layer-item--object ${isSelected ? 'layer-item--selected' : ''} ${isDragging ? 'layer-item--dragging' : ''} ${isDragOverAbove ? 'layer-item--dragover-above' : ''} ${isDragOverBelow ? 'layer-item--dragover-below' : ''}`}
        style={{ paddingLeft: `${24 + depth * 16}px` }}
        onClick={(e) => { e.stopPropagation(); onSelectItem?.(item.id, item.type); }}
        draggable={!readOnly}
        onDragStart={readOnly ? undefined : (e) => handleItemDragStart(e, item.id, item.type)}
        onDragOver={readOnly ? undefined : (e) => handleItemDragOver(e, item.id)}
        onDragLeave={readOnly ? undefined : handleItemDragLeave}
        onDrop={readOnly ? undefined : (e) => handleItemDrop(e, item.id, item.layerId)}
        onDragEnd={readOnly ? undefined : handleItemDragEnd}
      >
        <div className="layer-item-icon">
          <i className={`fas ${getItemIcon(item.type)}`}></i>
        </div>
        <span className="layer-item-name">{item.name}</span>
      </div>
    );
  };

  /**
   * Render a single layer item
   */
  const renderLayerItem = (layer: Layer, depth: number): JSX.Element => {
    const isActive = layer.id === activeLayerId;
    const isEditing = editingLayerId === layer.id;
    const isDefaultLayer = layer.id === 'default';
    const canDelete = layers.length > 1 && !isDefaultLayer;
    const isDragging = draggedLayerId === layer.id;
    const isDragOverAbove = dragOverInfo?.layerId === layer.id && dragOverInfo?.position === 'above';
    const isDragOverBelow = dragOverInfo?.layerId === layer.id && dragOverInfo?.position === 'below';
    const isDragOverInside = dragOverInfo?.layerId === layer.id && dragOverInfo?.position === 'inside';
    const isItemDropTarget = itemDragOverLayerId === layer.id;
    const layerHasChildren = hasChildren(layer.id);
    const hasItems = !readOnly && layerHasItems(layer.id);
    const isCollapsed = collapsedLayers.has(layer.id);
    const isItemsExpanded = expandedItemLayers.has(layer.id);
    const parentHidden = isParentHidden(layer.id);
    const effectivelyHidden = !layer.visible || parentHidden;

    return (
      <div
        key={layer.id}
        className={`layer-item ${isActive ? 'layer-item--active' : ''} ${effectivelyHidden ? 'layer-item--hidden' : ''} ${isDragging ? 'layer-item--dragging' : ''} ${isDragOverAbove ? 'layer-item--dragover-above' : ''} ${isDragOverBelow ? 'layer-item--dragover-below' : ''} ${isDragOverInside ? 'layer-item--dragover-inside' : ''} ${isItemDropTarget ? 'layer-item--item-drop-target' : ''}`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
        onClick={() => onSelectLayer(layer.id)}
        draggable={!readOnly && !isEditing && !isDefaultLayer}
        onDragStart={readOnly || isDefaultLayer ? undefined : (e) => handleDragStart(e, layer.id)}
        onDragOver={readOnly ? undefined : (e) => handleDragOver(e, layer.id)}
        onDragLeave={readOnly ? undefined : handleDragLeave}
        onDrop={readOnly ? undefined : (e) => handleDrop(e, layer.id)}
        onDragEnd={readOnly ? undefined : handleDragEnd}
      >
        {/* Expand/collapse button for layers with children or items */}
        {layerHasChildren || hasItems ? (
          <button
            className="layer-expand-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (layerHasChildren) {
                toggleCollapsed(layer.id);
              }
              if (hasItems) {
                toggleItemsExpanded(layer.id);
              }
            }}
            title={isCollapsed || !isItemsExpanded ? 'Expand' : 'Collapse'}
          >
            <i className={`fas fa-chevron-${(isCollapsed && layerHasChildren) || (!isItemsExpanded && hasItems) ? 'right' : 'down'}`}></i>
          </button>
        ) : (
          <div className="layer-expand-placeholder"></div>
        )}

        {/* Drag handle - only show in edit mode, not for default layer */}
        {!readOnly && !isDefaultLayer ? (
          <div className="layer-drag-handle" title="Drag to reorder or nest">
            <i className="fas fa-grip-vertical"></i>
          </div>
        ) : !readOnly ? (
          <div className="layer-expand-placeholder"></div>
        ) : null}

        {/* Visibility toggle */}
        <button
          className={`layer-visibility ${layer.visible ? 'layer-visibility--visible' : ''}`}
          onClick={(e) => { e.stopPropagation(); onToggleVisibility(layer.id); }}
          title={layer.visible ? 'Hide layer' : 'Show layer'}
        >
          <i className={`fas ${layer.visible ? 'fa-eye' : 'fa-eye-slash'}`}></i>
        </button>

        {/* Layer name */}
        <div className="layer-info">
          {isEditing && !readOnly && !isDefaultLayer ? (
            <input
              ref={editInputRef}
              type="text"
              className="layer-name-input"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onKeyDown={handleEditKeyDown}
              onBlur={handleRename}
              onClick={(e) => e.stopPropagation()}
              maxLength={50}
            />
          ) : (
            <span
              className="layer-name"
              onDoubleClick={readOnly || isDefaultLayer ? undefined : (e) => { e.stopPropagation(); startEditing(layer); }}
            >
              {layer.name}
            </span>
          )}
        </div>

        {/* Opacity text input */}
        <div className="layer-opacity" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            className="layer-opacity-input"
            value={`${layer.opacity}%`}
            onChange={(e) => {
              const val = e.target.value.replace('%', '');
              handleOpacityInputChange(layer.id, val);
            }}
            onFocus={(e) => {
              e.target.select();
            }}
            onBlur={(e) => {
              const val = e.target.value.replace('%', '');
              handleOpacityBlur(layer.id, val);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                (e.target as HTMLInputElement).blur();
              }
            }}
          />
        </div>

        {/* Edit description button - only show in edit mode and not for default layer */}
        {!readOnly && !isDefaultLayer && onEditDescription && (
          <button
            className={`layer-action-btn layer-action-btn--description ${layer.description ? 'has-description' : ''}`}
            onClick={(e) => { e.stopPropagation(); onEditDescription(layer.id); }}
            title={layer.description ? 'Edit description' : 'Add description'}
          >
            <i className="fas fa-file-alt"></i>
          </button>
        )}

        {/* Delete button - only show in edit mode */}
        {!readOnly && (
          <button
            className="layer-action-btn layer-action-btn--delete"
            onClick={(e) => { e.stopPropagation(); setDeleteConfirmLayerId(layer.id); }}
            disabled={!canDelete}
            title={isDefaultLayer ? 'Cannot delete default layer' : (canDelete ? 'Delete layer' : 'Cannot delete last layer')}
          >
            <i className="fas fa-trash"></i>
          </button>
        )}
      </div>
    );
  };

  /**
   * Render layers recursively
   */
  const renderLayers = (parentId: string | null, depth: number): JSX.Element[] => {
    const children = getChildren(parentId);
    const elements: JSX.Element[] = [];

    for (const layer of children) {
      elements.push(renderLayerItem(layer, depth));

      // Render items if expanded (only in edit mode)
      if (!readOnly && expandedItemLayers.has(layer.id)) {
        const layerItemsList = getLayerItems(layer.id);
        for (const item of layerItemsList) {
          elements.push(renderItem(item, depth));
        }
      }

      // Render children if not collapsed
      if (!collapsedLayers.has(layer.id)) {
        elements.push(...renderLayers(layer.id, depth + 1));
      }
    }

    return elements;
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="layer-selector">
      <div className="layer-selector-header">
        <span className="layer-selector-title">Layers</span>
        <div className="layer-selector-actions">
          {!readOnly && (
            <button
              className="layer-selector-btn"
              onClick={() => { setAddingToParentId(null); setIsAddingLayer(true); }}
              title="Add Layer"
            >
              <i className="fas fa-plus"></i>
            </button>
          )}
          <button className="layer-selector-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>

      <div className="layer-selector-content">
        {/* Add new layer input - only in edit mode */}
        {!readOnly && isAddingLayer && (
          <div className="layer-add-input" style={{ paddingLeft: addingToParentId ? '24px' : '8px' }}>
            {addingToParentId && (
              <span className="layer-add-parent-indicator">
                <i className="fas fa-level-down-alt"></i>
              </span>
            )}
            <input
              ref={addInputRef}
              type="text"
              value={newLayerName}
              onChange={(e) => setNewLayerName(e.target.value)}
              onKeyDown={handleAddKeyDown}
              onBlur={() => {
                if (!newLayerName.trim()) {
                  setIsAddingLayer(false);
                  setAddingToParentId(null);
                }
              }}
              placeholder={addingToParentId ? 'Sub-layer name...' : 'Layer name...'}
              maxLength={50}
            />
            <button onClick={handleAddLayer} disabled={!newLayerName.trim()}>
              <i className="fas fa-check"></i>
            </button>
            <button onClick={() => { setIsAddingLayer(false); setNewLayerName(''); setAddingToParentId(null); }}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Layer list */}
        <div className="layer-list">
          {renderLayers(null, 0)}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirmLayerId && (() => {
        const layerToDelete = layers.find(l => l.id === deleteConfirmLayerId);
        const layerHasChildLayers = hasChildren(deleteConfirmLayerId);

        return (
          <Modal
            isOpen={true}
            onClose={() => setDeleteConfirmLayerId(null)}
            title="Delete Layer"
          >
            <div className="layer-delete-confirm">
              <p>Delete layer "{layerToDelete?.name}"?</p>
              <div className="layer-delete-actions layer-delete-actions--vertical">
                <button
                  className="layer-delete-cancel"
                  onClick={() => setDeleteConfirmLayerId(null)}
                >
                  Cancel
                </button>
                <button
                  className="layer-delete-confirm-btn layer-delete-confirm-btn--secondary"
                  onClick={() => handleDeleteConfirm(false, false)}
                >
                  Delete layer only
                </button>
                <button
                  className="layer-delete-confirm-btn"
                  onClick={() => handleDeleteConfirm(layerHasChildLayers, true)}
                >
                  Delete layer and children
                </button>
              </div>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}

export default LayerSelector;
